"use client"

import { observer } from "mobx-react-lite"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface SimpleBarChartProps {
  title: string
  description?: string
  data: Array<{
    label: string
    value: number
  }>
  theme?: ThemeConfigType
  className?: string
}

export const SimpleBarChart = observer(
  ({ title, description, data, theme, className }: SimpleBarChartProps) => {
    const activeTheme = theme || getDefaultTheme()
    const themeClasses = useTheme(activeTheme)
    const isDefaultTheme = !theme

    // Chart config
    const chartConfig: ChartConfig = {
      value: {
        label: "Value",
        color: "var(--chart-3)", // Blue color from theme palette
      },
    }

    return (
      <Card
        className={cn(
          "w-full overflow-hidden",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
      >
        <CardHeader>
          <CardTitle className={cn(!isDefaultTheme && themeClasses.typography)}>{title}</CardTitle>
          {description && (
            <p
              className={cn(
                "text-sm text-muted-foreground",
                !isDefaultTheme && themeClasses.typography,
              )}
            >
              {description}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={chartConfig}
            className="h-[300px] sm:h-[350px] md:h-[400px] w-full"
          >
            <BarChart data={data} accessibilityLayer>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tickLine={false}
                tickMargin={8}
                axisLine={false}
                tick={{ fontSize: "12px" }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: "12px" }} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="value" fill="var(--color-value)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    )
  },
)

SimpleBarChart.displayName = "SimpleBarChart"
